import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { useQuery } from '@apollo/client';
import { setError } from '../../../actions';
import { useFullPhoneNumbers } from '../../../hooks/useFullPhoneNumbers';
import { AgencyPageSchema } from './AgencyPageSchema';
import { AnalyticLogTargetTypeEnum } from '../../../graphql/types';
import { getSelectedAgencyQuery } from '../../../api/queries';
import {
  AgencyContent,
  AgencyItemsWrapper,
  AgencyLayout,
  BottomPhoneWrapper,
  HeaderWrapper
} from './styled';
import WhiteHeader from '../../WhiteHeader';
import { SelectedAgencyQuery } from '../../../graphql/graphql';
import { AgencyInfoBlock } from '../../Agency/AgencyInfoBlock';
import useScreenSize from '../../../hooks/useScreenSize';
import { useClient } from '../../../hooks/useClient';

const AgencyFilter = dynamic(
  () => import('../../Agency/AgencyFilter').then(mod => mod.AgencyFilter),
  {
    ssr: false
  }
);

const BottomPhoneButton = dynamic(
  () => import('../../Ad/BottomPhoneButton').then(mod => mod.BottomPhoneButton),
  {
    ssr: false
  }
);

interface AgencyPageProps {
  agencySlug: string;
  hasError?: boolean;
}

export const AgencyPage: React.FC<AgencyPageProps> = ({
  agencySlug,
  hasError = false
}) => {
  const dispatch = useDispatch();
  const { isSmallOrLargeDesktop } = useScreenSize();
  const isClient = useClient();

  const { data, error } = useQuery<SelectedAgencyQuery>(
    getSelectedAgencyQuery,
    {
      variables: { slug: agencySlug }
    }
  );

  const agencyData = data?.agency;
  const categories = data?.categories;

  useEffect(() => {
    if (hasError || error) dispatch(setError(true));
  }, [hasError, dispatch, error]);

  const { onClickHandler, phonesShown, setIsVisible, isVisible } =
    useFullPhoneNumbers({
      id: agencySlug,
      residenceId: null,
      path: 'agentlikler',
      targetType: AnalyticLogTargetTypeEnum.Agency,
      agencyId: agencyData?.id
    });

  if (!agencyData) return null;

  const isShowFilters = !!agencyData.itemsCount;

  return (
    <>
      {agencyData && <AgencyPageSchema agencyData={agencyData} />}
      <HeaderWrapper>
        <WhiteHeader isShowBackButton />
      </HeaderWrapper>
      <AgencyLayout>
        <AgencyContent>
          <AgencyInfoBlock
            agencyData={agencyData}
            onClickHandler={onClickHandler}
            phonesShown={phonesShown}
            isVisible={isVisible}
            setIsVisible={setIsVisible}
          />
          {isShowFilters && (
            <AgencyItemsWrapper>
              <AgencyFilter
                categories={agencyData.itemCategories}
                allCategories={categories}
                agencyId={agencyData.id}
              />
            </AgencyItemsWrapper>
          )}
        </AgencyContent>
      </AgencyLayout>
      {(!isSmallOrLargeDesktop || !isClient) && (
        <BottomPhoneWrapper>
          <BottomPhoneButton
            phones={phonesShown}
            clickHandler={onClickHandler}
            {...{ setIsVisible, isVisible }}
          />
        </BottomPhoneWrapper>
      )}
    </>
  );
};
